"use client";

import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface InViewFadeProps {
  children: React.ReactNode;
  className?: string;
}

// Sets `data-inview` on the wrapper once it scrolls into view so descendants
// can run entrance transitions via `group-data-[inview]:*` classes. Children
// stay server-rendered (passed through as RSC children); only the observer is
// client-side. Falls back to visible-immediately when IntersectionObserver is
// entirely absent (very old browsers). Note: jest does NOT hit that branch —
// jest.setup.ts installs a no-op IntersectionObserver stub, so under test the
// wrapper simply never gets data-inview (jsdom applies no CSS, so content is
// still queryable).
const InViewFade: React.FC<InViewFadeProps> = ({ children, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "-80px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      data-inview={inView ? "" : undefined}
      className={cn("group", className)}
    >
      {children}
    </div>
  );
};

export default InViewFade;
